/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import type { Workspace } from '../../../types/workspace'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import {
  getEduSessionRoll,
  markEduAttendance,
  type RollLearner,
  type MarkAttendanceItem,
} from '../../../services/eduAttendanceService'
import { queueMemberAttendanceMark } from '../../../services/memberOutboxService'
import { RollCallRow, type RollCallStatus } from '../../Common/RollCallRow'
import { RollSummaryBar, type RollSummaryCounts } from '../../Common/RollSummaryBar'
import { SectionLoader } from '../../Common/SectionLoader'
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded'
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded'
import './classTab.css'

export interface ClassRegisterProps {
  workspace: Workspace
  seriesId?: string
  date?: string
  userId?: string | null
  className?: string
  onBack?: () => void
  sessionTitle?: string
}

type RegisterError = { kind: 'text'; text: string } | { kind: 'key'; key: NlsKey }

export const ClassRegister: React.FC<ClassRegisterProps> = ({
  workspace,
  seriesId = '',
  date = new Date().toISOString().slice(0, 10),
  userId,
  className,
  sessionTitle,
}) => {
  const { t } = useNls()
  const displayTitle = sessionTitle || t('NLSEduMobileSessionRegister')
  const [learners, setLearners] = useState<RollLearner[]>([])
  const [statusMap, setStatusMap] = useState<Record<string, RollCallStatus>>({})
  const [onLeaveSet, setOnLeaveSet] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<RegisterError | null>(null)
  const [busy, setBusy] = useState<boolean>(false)
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [savesOffline, setSavesOffline] = useState<boolean>(false)

  const loadRoll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getEduSessionRoll({
        workspaceId: workspace.id,
        seriesId,
        date,
      })
      const list = res.learners || []
      setLearners(list)

      const leaveSet = new Set(res.onLeaveLearnerIds || [])
      setOnLeaveSet(leaveSet)

      const initialMap: Record<string, RollCallStatus> = {}
      for (const learner of list) {
        if (leaveSet.has(learner.learnerId)) {
          initialMap[learner.learnerId] = 'OL'
        } else if (res.marks && res.marks[learner.learnerId]) {
          const raw = res.marks[learner.learnerId]
          initialMap[learner.learnerId] =
            raw === 'PRESENT' ? 'P' : raw === 'ABSENT' ? 'A' : raw === 'LATE' ? 'L' : 'OL'
        } else {
          // EM-P2: Everyone starts Present
          initialMap[learner.learnerId] = 'P'
        }
      }
      setStatusMap(initialMap)

      if (res.session?.rollTakenAt) {
        setSubmitted(true)
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? { kind: 'text', text: err.message }
          : { kind: 'key', key: 'NLSEduMobileLoadRollError' },
      )
    } finally {
      setLoading(false)
    }
  }, [workspace.id, seriesId, date])

  useEffect(() => {
    void loadRoll()
  }, [loadRoll])

  const counts: RollSummaryCounts = useMemo(() => {
    let present = 0
    let absent = 0
    let late = 0
    let onLeave = 0
    for (const learner of learners) {
      const st = statusMap[learner.learnerId] || 'P'
      if (st === 'P') present++
      else if (st === 'A') absent++
      else if (st === 'L') late++
      else if (st === 'OL') onLeave++
    }
    return { present, absent, late, onLeave }
  }, [learners, statusMap])

  const handleStatusChange = (learnerId: string, nextStatus: RollCallStatus) => {
    if (onLeaveSet.has(learnerId)) return
    setStatusMap((prev) => ({
      ...prev,
      [learnerId]: nextStatus,
    }))
  }

  const handleSubmit = async () => {
    setBusy(true)
    const marks: MarkAttendanceItem[] = learners.map((l) => {
      const st = statusMap[l.learnerId] || 'P'
      return {
        learnerId: l.learnerId,
        status:
          st === 'P'
            ? 'PRESENT'
            : st === 'A'
              ? 'ABSENT'
              : st === 'L'
                ? 'LATE'
                : 'ON_LEAVE',
      }
    })

    const payload = {
      workspaceId: workspace.id,
      seriesId,
      date,
      marks,
    }

    try {
      await markEduAttendance(payload)
      setSubmitted(true)
      setSavesOffline(false)
    } catch {
      try {
        await queueMemberAttendanceMark(payload, {
          uid: userId,
          workspaceId: workspace.id,
          workspaceName: workspace.name,
        })
        setSubmitted(true)
        setSavesOffline(true)
      } catch (queueErr) {
        setError(
          queueErr instanceof Error
            ? { kind: 'text', text: queueErr.message }
            : { kind: 'key', key: 'NLSEduMobileSaveAttendanceError' },
        )
      }
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <SectionLoader />
  }

  return (
    <div className={['classRegister', className].filter(Boolean).join(' ')}>
      <div className="classRegister__header">
        <div className="classRegister__headerRow">
          <span className="classRegister__title">{displayTitle}</span>
          <span
            className="classRegister__offlineStatus"
            data-testid="register-offline-indicator"
          >
            {savesOffline ? (
              <>
                <CloudQueueRoundedIcon fontSize="small" /> {t('NLSEduMobileSavesOffline')}
              </>
            ) : (
              <>
                <CloudDoneRoundedIcon fontSize="small" /> {t('NLSEduMobileSynced')}
              </>
            )}
          </span>
        </div>
        <p className="classRegister__hint">
          {t('NLSEduMobileRegisterHint', { count: learners.length })}
        </p>
      </div>

      {error ? (
        <div className="classTab__errorNotice" role="alert">
          {error.kind === 'key' ? t(error.key) : error.text}
        </div>
      ) : null}

      <div className="classRegister__list" data-testid="class-register-list">
        {learners.map((learner, idx) => {
          const st = statusMap[learner.learnerId] || 'P'
          const isLeave = onLeaveSet.has(learner.learnerId)
          return (
            <div key={learner.learnerId} className="classRegister__rowWrapper">
              <RollCallRow
                rollNumber={learner.rollNo || String(idx + 1).padStart(2, '0')}
                name={learner.displayName}
                status={st}
                locked={isLeave}
                onChange={(next) => handleStatusChange(learner.learnerId, next)}
                testId={`roll-row-${learner.learnerId}`}
              />
              {isLeave ? (
                <div className="classRegister__leaveReason">
                  {t('NLSEduMobileApprovedLeaveNotice')}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>

      <div className="classRegister__summary">
        <RollSummaryBar
          counts={counts}
          actionLabel={submitted ? t('NLSEduMobileUpdateRegister') : t('NLSEduMobileSubmitRegister')}
          onAction={handleSubmit}
          busy={busy}
          testId="class-register-summary-bar"
        />
      </div>
    </div>
  )
}

export default ClassRegister
